import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Button, Space, Grid, Drawer } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DollarOutlined,
  WalletOutlined,
  TransactionOutlined,
  HomeOutlined,
  AppstoreOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

interface AdminLayoutProps {
  children: React.ReactNode;
}

const { useBreakpoint } = Grid;

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const screens = useBreakpoint();
  const isMobile = !screens.md; // < 768px

  // Auto-collapse on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: 'Back to Site',
    },
    {
      type: 'divider' as const,
    },
    {
      key: '/admin',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/admin/users',
      icon: <UserOutlined />,
      label: 'User Management',
    },
    {
      key: '/admin/quotations',
      icon: <FileTextOutlined />,
      label: 'Orders',
    },
    {
      key: '/admin/products',
      icon: <ShoppingOutlined />,
      label: 'Products',
    },
    {
      key: '/admin/categories',
      icon: <AppstoreOutlined />,
      label: 'Categories',
    },
    {
      key: 'billing',
      icon: <DollarOutlined />,
      label: 'Billing',
      children: [
        {
          key: '/admin/billing',
          icon: <DashboardOutlined />,
          label: 'Overview',
        },
        {
          key: '/admin/billing/transactions',
          icon: <TransactionOutlined />,
          label: 'Transactions',
        },
        {
          key: '/admin/billing/invoices',
          icon: <FileTextOutlined />,
          label: 'Invoices',
        },
        // Subscriptions menu hidden — see #24 (wallet replaces subscriptions)
      ],
    },
    {
      key: '/admin/wallet/tiers',
      icon: <WalletOutlined />,
      label: 'Wallet Tiers',
    },
    {
      key: '/admin/promo-codes',
      icon: <TagOutlined />,
      label: 'Promo Codes',
    },
    {
      key: '/admin/email-templates',
      icon: <MailOutlined />,
      label: 'Email Templates',
    },
    {
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      key: '/admin/user-guide',
      icon: <QuestionCircleOutlined />,
      label: 'User Guide',
    },
  ];

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  // Get current selected menu key based on location
  const getCurrentMenuKey = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/') {
      return '/admin';
    }
    return path;
  };

  // Menu content shared between Sider and Drawer
  const menuContent = (
    <>
      <div style={{
        padding: '16px',
        textAlign: 'center',
        borderBottom: '1px solid rgba(255, 174, 0, 0.3)',
        background: '#121512',
      }}>
        {collapsed && !isMobile ? (
          <Avatar size="large" style={{ backgroundColor: '#ffae00', color: '#ffffff' }}>
            DE
          </Avatar>
        ) : (
          <div>
            <Avatar size="large" style={{ backgroundColor: '#ffae00', color: '#ffffff' }}>
              DE
            </Avatar>
            <div style={{ color: '#ffae00', marginTop: '8px', fontSize: '14px', fontWeight: 500 }}>
              DasoonEgg Admin
            </div>
          </div>
        )}
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getCurrentMenuKey()]}
        items={menuItems}
        onClick={({ key }) => {
          handleMenuClick(key);
          if (isMobile) setMobileDrawerOpen(false);
        }}
        style={{ border: 'none', background: '#2B302B' }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sider - hidden on mobile */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme="dark"
          width={250}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: '#2B302B',
          }}
        >
          {menuContent}
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        placement="left"
        onClose={() => setMobileDrawerOpen(false)}
        open={mobileDrawerOpen}
        width={250}
        styles={{
          body: { padding: 0, background: '#2B302B' },
          header: { display: 'none' },
        }}
      >
        {menuContent}
      </Drawer>

      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 250), transition: 'all 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#2B302B',
            borderBottom: '2px solid #ffae00',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Space>
            <Button
              type="text"
              icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
              onClick={() => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
                color: '#ffffff',
              }}
            />
          </Space>

          <Space>
            <span style={{ marginRight: '16px', color: '#ffffff' }}>
              Welcome, <strong style={{ color: '#ffae00' }}>{user?.name || 'Admin'}</strong>
            </span>
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: ({ key }) => {
                  const item = userMenuItems.find(item => item.key === key);
                  if (item?.onClick) {
                    item.onClick();
                  }
                }
              }}
              placement="bottomRight"
            >
              <Avatar
                style={{ backgroundColor: '#ffae00', cursor: 'pointer', color: '#ffffff' }}
                icon={<UserOutlined />}
              />
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: 0,
            padding: isMobile ? '12px' : 0,
            minHeight: 'calc(100vh - 64px)',
            background: '#f0f2f5',
            overflow: 'auto',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;